import api from '../axiosConfig';
import { INVENTORY } from '../config/apiEndpoints';

export const InventoryService = {
    /**
     * List all inventory items
     * GET /api/v1/inventory/items
     */
    getInventoryItems: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(INVENTORY.ITEMS, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService getInventoryItems error:', error);
            throw error;
        }
    },

    /**
     * Create a new inventory item
     * POST /api/v1/inventory/items
     * @param {Object} data - Inventory item payload
     */
    createInventoryItem: async (data) => {
        try {
            const response = await api.post(INVENTORY.ITEMS, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService createInventoryItem error:', error);
            throw error;
        }
    },

    /**
     * Get inventory item by ID
     * GET /api/v1/inventory/items/:id
     * @param {string} id - Item ID
     */
    getInventoryItemById: async (id) => {
        try {
            const response = await api.get(INVENTORY.ITEM_BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService getInventoryItemById error:', error);
            throw error;
        }
    },

    /**
     * Update an inventory item
     * PUT /api/v1/inventory/items/:id
     * @param {string} id - Item ID
     * @param {Object} data - Update payload (name, unitCost)
     */
    updateInventoryItem: async (id, data) => {
        try {
            const response = await api.put(INVENTORY.ITEM_BY_ID(id), data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService updateInventoryItem error:', error);
            throw error;
        }
    },

    /**
     * Delete an inventory item
     * DELETE /api/v1/inventory/items/:id
     * @param {string} id - Item ID
     */
    deleteInventoryItem: async (id) => {
        try {
            const response = await api.delete(INVENTORY.ITEM_BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService deleteInventoryItem error:', error);
            throw error;
        }
    },

    /**
     * Adjust inventory item stock quantity
     * POST /api/v1/inventory/items/:id/adjust
     * @param {string} id - Item ID
     * @param {Object} data - Adjustment payload (quantity, reason, notes)
     */
    adjustInventoryItemStock: async (id, data) => {
        try {
            const response = await api.post(INVENTORY.ITEM_ADJUST(id), data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService adjustInventoryItemStock error:', error);
            throw error;
        }
    },

    /**
     * Get inventory item movement logs
     * GET /api/v1/inventory/items/:id/movements
     * @param {string} id - Item ID
     * @param {Object} params - Query parameters (page, limit)
     */
    getInventoryItemMovements: async (id, params = {}) => {
        try {
            const response = await api.get(INVENTORY.ITEM_MOVEMENTS(id), { params });
            // Keep the pagination data if available, or return the whole response data
            return response.data;
        } catch (error) {
            console.error('InventoryService getInventoryItemMovements error:', error);
            throw error;
        }
    },

    /**
     * Get list of purchase orders
     * GET /api/v1/inventory/po
     */
    getPurchaseOrders: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(INVENTORY.PURCHASE_ORDERS, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService getPurchaseOrders error:', error);
            throw error;
        }
    },

    /**
     * Create a purchase order
     * POST /api/v1/inventory/po
     * @param {Object} data - PO payload
     */
    createPurchaseOrder: async (data) => {
        try {
            const response = await api.post(INVENTORY.PURCHASE_ORDERS, data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService createPurchaseOrder error:', error);
            throw error;
        }
    },

    /**
     * Get purchase order details by ID
     * GET /api/v1/inventory/po/:id
     * @param {string} id - PO ID
     */
    getPurchaseOrderById: async (id) => {
        try {
            const response = await api.get(INVENTORY.PO_BY_ID(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService getPurchaseOrderById error:', error);
            throw error;
        }
    },

    /**
     * Submit a purchase order
     * POST /api/v1/inventory/po/:id/submit
     * @param {string} id - PO ID
     */
    submitPurchaseOrder: async (id) => {
        try {
            const response = await api.post(INVENTORY.PO_SUBMIT(id));
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService submitPurchaseOrder error:', error);
            throw error;
        }
    },

    /**
     * Approve or reject a purchase order
     * POST /api/v1/inventory/po/:id/approve
     * @param {string} id - PO ID
     * @param {Object} data - { status: 'Approved' | 'Rejected', rejectionReason: string }
     */
    approveRejectPurchaseOrder: async (id, data) => {
        try {
            const response = await api.post(INVENTORY.PO_APPROVE(id), data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService approveRejectPurchaseOrder error:', error);
            throw error;
        }
    },

    /**
     * Record delivery receipt for a purchase order
     * POST /api/v1/inventory/po/:id/receive
     * @param {string} id - PO ID
     * @param {Object} data - { supplierInvoiceNumber: string, items: Array<{ itemId, quantityReceived }> }
     */
    receivePurchaseOrder: async (id, data) => {
        try {
            const response = await api.post(INVENTORY.PO_RECEIVE(id), data);
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('InventoryService receivePurchaseOrder error:', error);
            throw error;
        }
    }
};
